import { ApplicationConfig, importProvidersFrom, InjectionToken, LOCALE_ID, provideZoneChangeDetection, inject, provideAppInitializer } from '@angular/core';
import {MatToolbarModule} from '@angular/material/toolbar';
import {MatTabsModule} from '@angular/material/tabs';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {MatSnackBarModule} from '@angular/material/snack-bar';
import {MatExpansionModule} from '@angular/material/expansion';
import {TranslateLoader, TranslateModule} from '@ngx-translate/core';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {MatButtonModule} from '@angular/material/button';
import {
  PreloadAllModules,
  provideRouter,
  withInMemoryScrolling,
  withPreloading,
  withViewTransitions,
} from '@angular/router';
import {routes} from './app.routes';
import {ProgressInterceptor} from './progress.interceptor';
import {ErrorInterceptor} from './error.interceptor';
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE,} from '@angular/material/core';
import {MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter,} from '@angular/material-moment-adapter';
import {provideAnimations} from '@angular/platform-browser/animations';
import {ConfigService} from './core/services/config.service';
import {AuthService} from './auth/auth.service';
import {JWT_OPTIONS, JwtModule} from '@auth0/angular-jwt';
import {AppConfig} from "./core/types/appConfig.types";
import {DatadogService} from "./datadog.service";
import {registerLocaleData} from "@angular/common";
import {provideStore} from '@ngrx/store';
import {provideCardataWizard, cardataTranslateLoaderFactory} from './tds-wizard/provide-cardata-wizard';

export const APP_CONFIG = new InjectionToken<AppConfig>('app.config');

const MY_DATE_FORMAT = {
  parse: {
    dateInput: 'DD/MM/YYYY', // this is how your date will be parsed from Input
  },
  display: {
    dateInput: 'DD.MM.YYYY', // this is how your date will get displayed on the Input
    monthYearLabel: 'MMMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

export function initializeAppFactory(
  configService: ConfigService,
  datadogService: DatadogService,
): () => Promise<void> {
  console.log("Initializing App Factory");
  return () =>
    configService.loadAppConfig()
      .then(() => {
        registerLocaleData(configService.getLocaleData());
        return datadogService.init();
      })
}

export function jwtOptionsFactory() {
  return {
    tokenGetter: AuthService.getRawToken,
    allowedDomains: ConfigService.getConfig().jwtWhiteListedDomains,
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({eventCoalescing: true}),
    provideStore(),
    provideCardataWizard(),
    importProvidersFrom(
      MatToolbarModule,
      MatTabsModule,
      MatProgressBarModule,
      MatSnackBarModule,
      MatExpansionModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: cardataTranslateLoaderFactory,
          deps: [HttpClient],
        },
      }),
      MatButtonModule,
    ),
    provideRouter(
      routes,
      withInMemoryScrolling({scrollPositionRestoration: 'enabled'}),
      withPreloading(PreloadAllModules),
      withViewTransitions(),
    ),
    {provide: HTTP_INTERCEPTORS, useClass: ProgressInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true},
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    {provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: {useUtc: true}},
    {provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMAT},
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    provideAppInitializer(() => {
        const initializerFn = (initializeAppFactory)(inject(ConfigService), inject(DatadogService));
        return initializerFn();
      }),
    {
      provide: APP_CONFIG,
      useFactory: () => ConfigService.getConfig(),
    },
    {
      provide: LOCALE_ID,
      useFactory: () => ConfigService.getConfig().localeId,
    },
    importProvidersFrom(
      JwtModule.forRoot({
        jwtOptionsProvider: {
          provide: JWT_OPTIONS,
          useFactory: jwtOptionsFactory,
          deps: [ConfigService],
        },
      }),
    ),
  ],
};
